/**
 * 커스텀 날짜·시간 선택 위젯용 계산 유틸 (순수 함수).
 *
 * Date의 로컬/UTC 함정을 피하기 위해 연·월·일 숫자와 "YYYY-MM-DD" 문자열만 다룬다.
 * 요일 계산은 항상 `Date.UTC`를 거쳐 타임존과 무관하게 결정적으로 처리한다.
 */

export const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"] as const;
export const MINUTE_OPTIONS = [0, 10, 20, 30, 40, 50] as const;

/** 오전/오후 표기. */
export const PERIODS = ["오전", "오후"] as const;
export type Period = (typeof PERIODS)[number];
/** 12시간제 시 옵션(1~12). */
export const HOUR12_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

/** 24시간(0~23) → 오전/오후 + 12시간(1~12). 0시→오전 12, 12시→오후 12. */
export function to12Hour(hour24: number): { period: Period; hour12: number } {
  const period: Period = hour24 < 12 ? "오전" : "오후";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { period, hour12 };
}

/** 오전/오후 + 12시간(1~12) → 24시간(0~23). 오전 12→0, 오후 12→12. */
export function to24Hour(period: Period, hour12: number): number {
  const base = hour12 % 12; // 12 → 0
  return period === "오전" ? base : base + 12;
}

/** 달력 한 칸. `date`는 "YYYY-MM-DD", `inMonth`는 현재 보고 있는 달 소속 여부. */
export interface CalendarCell {
  date: string;
  day: number;
  inMonth: boolean;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** (year, month 1~12, day) → "YYYY-MM-DD" */
function toDateString(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** 해당 월의 일수. month는 1~12. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 월 이동(경계 안전). month는 1~12, 반환도 1~12. */
export function addMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const zeroBased = month - 1 + delta;
  const nextYear = year + Math.floor(zeroBased / 12);
  const nextMonth = ((zeroBased % 12) + 12) % 12;
  return { year: nextYear, month: nextMonth + 1 };
}

/**
 * 6주 × 7일(일요일 시작) 달력 행렬. 앞뒤 달 날짜로 빈칸을 채운다.
 * month는 1~12.
 */
export function getMonthMatrix(year: number, month: number): CalendarCell[][] {
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay(); // 0=일
  const totalDays = daysInMonth(year, month);

  const prev = addMonth(year, month, -1);
  const prevDays = daysInMonth(prev.year, prev.month);
  const next = addMonth(year, month, 1);

  const cells: CalendarCell[] = [];

  // 이전 달 채움
  for (let i = 0; i < firstWeekday; i++) {
    const day = prevDays - firstWeekday + 1 + i;
    cells.push({ date: toDateString(prev.year, prev.month, day), day, inMonth: false });
  }
  // 이번 달
  for (let day = 1; day <= totalDays; day++) {
    cells.push({ date: toDateString(year, month, day), day, inMonth: true });
  }
  // 다음 달 채움 (총 42칸)
  let nextDay = 1;
  while (cells.length < 42) {
    cells.push({
      date: toDateString(next.year, next.month, nextDay),
      day: nextDay,
      inMonth: false,
    });
    nextDay++;
  }

  const weeks: CalendarCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

/** "YYYY-MM-DD" → "2026-06-05 (금)". 요일은 Date.UTC 기반 결정적 계산. */
export function formatDateLabel(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${date} (${WEEKDAY_LABELS[weekday]})`;
}
